import {
  ToolbarColorSelect,
  ToolbarFontFamilySelect,
  ToolbarFontSizeSelect,
  ToolbarGroup,
  ToolbarHeadingToggles,
  ToolbarParagraphSpacingSelect,
  ToolbarTextAlignGroup,
} from "@/components/tiptap/ToolbarGroups";
import {
  TOOLBAR_CONTROL_CLASS,
  TOOLBAR_ICON_CLASS,
} from "@/components/tiptap/toolbar.constants";
import {
  presetShowsBulletList,
  presetShowsFontFamily,
  presetShowsHeadings,
  presetShowsReadMore,
  presetShowsSeparator,
  presetShowsSpacing,
  type ToolbarPreset,
} from "@/components/tiptap/toolbar-preset";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Toggle } from "@/components/ui/toggle";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { cn } from "@/lib/utils";
import type { Editor } from "@tiptap/react";
import {
  Bold,
  ChevronsDown,
  Italic,
  LinkIcon,
  List,
  Redo,
  SeparatorHorizontal,
  Underline as UnderlineIcon,
  Undo,
} from "lucide-react";

type Props = {
  editor: Editor | null;
  compact?: boolean;
  /**
   * Barre d'outils réduite (opt-in) : n'affiche QUE le lien. Utilisé par le bloc
   * RichText du CMS avancé — toute la mise en forme est gérée au niveau du bloc
   * via l'onglet Design (« Traitement du texte »).
   */
  minimal?: boolean;
  /** Preset d'outils (published / complete / compact). */
  toolbarPreset?: ToolbarPreset;
};

/** Positions (pos + taille) de tous les nœuds « Lire plus » du document. */
function getReadMorePositions(editor: Editor): { pos: number; size: number }[] {
  const positions: { pos: number; size: number }[] = [];
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === "readMore") {
      positions.push({ pos, size: node.nodeSize });
    }
  });
  return positions;
}

/**
 * Bascule le marqueur « Lire plus » : s'il en existe déjà un (n'importe où),
 * on le retire ; sinon on l'insère au curseur. Garantit au plus un marqueur.
 */
function toggleReadMore(editor: Editor): void {
  const positions = getReadMorePositions(editor);
  if (positions.length > 0) {
    const { tr } = editor.state;
    positions
      .sort((a, b) => b.pos - a.pos)
      .forEach(({ pos, size }) => tr.delete(pos, pos + size));
    editor.view.dispatch(tr);
    editor.commands.focus();
  } else {
    editor.chain().focus().insertContent({ type: "readMore" }).run();
  }
}

/**
 * Barre d'outils TipTap : tous les outils du preset sont toujours visibles et
 * se répartissent sur autant de lignes que nécessaire (`flex-wrap`).
 */
function ToolBar({
  editor,
  compact = false,
  minimal = false,
  toolbarPreset = "complete",
}: Props) {
  const vt = useVisibleT();

  if (!editor) {
    return null;
  }

  const handleLinkUpdate = (url: string, openInNewTab: boolean) => {
    if (url) {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({
          href: url,
          target: openInNewTab ? "_blank" : null,
        })
        .run();
    }
  };

  const controlClass = compact ? "h-8 shrink-0" : TOOLBAR_CONTROL_CLASS;
  const iconClass = compact ? "h-4 w-4" : TOOLBAR_ICON_CLASS;
  const iconButtonClass = cn(controlClass, compact ? "w-8 px-0" : "w-9 px-0");

  const linkPopover = (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={minimal ? "outline" : "ghost"}
          size="icon"
          aria-label={vt("bo.tiptap.link", "Lien")}
          title={vt("bo.tiptap.link", "Lien (URL)")}
          className={cn(
            minimal ? cn(controlClass, compact ? "w-8" : "w-9") : iconButtonClass,
            !minimal && "bg-transparent hover:bg-muted",
            editor.isActive("link") &&
              "bg-accent text-white hover:bg-accent hover:text-white",
          )}
        >
          <LinkIcon className={iconClass} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="link">{vt("bo.tiptap.linkUrl", "Lien URL")}</Label>
            <Input
              id="link"
              defaultValue={editor.getAttributes("link").href || ""}
              placeholder="https://example.com"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="newTab"
              defaultChecked={editor.getAttributes("link").target === "_blank"}
            />
            <Label htmlFor="newTab">
              {vt("bo.tiptap.openInNewTab", "Ouvrir dans un nouvel onglet")}
            </Label>
          </div>
          <div className="flex justify-between">
            <Button
              type="button"
              onClick={() => {
                const url = (
                  document.getElementById("link") as HTMLInputElement
                ).value;
                const newTab = (
                  document.getElementById("newTab") as HTMLInputElement
                )["ariaChecked"];
                handleLinkUpdate(url, newTab === "true");
              }}
            >
              {editor.isActive("link")
                ? vt("bo.tiptap.update", "Mettre à jour")
                : vt("bo.tiptap.add", "Ajouter")}
            </Button>
            {editor.isActive("link") && (
              <Button
                type="button"
                variant="destructive"
                onClick={() => editor.chain().focus().unsetLink().run()}
              >
                {vt("bo.tiptap.delete", "Supprimer")}
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );

  if (minimal) {
    return (
      <div
        className={cn(
          "flex w-full items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-50/80 p-0.5",
          compact ? "my-0 min-h-8" : "my-2 min-h-10 p-1",
        )}
      >
        <ToolbarGroup className="pl-0.5">{linkPopover}</ToolbarGroup>
      </div>
    );
  }

  const showFontFamily = presetShowsFontFamily(toolbarPreset);
  const showSpacing = presetShowsSpacing(toolbarPreset);
  const showBulletList = presetShowsBulletList(toolbarPreset);
  const showSeparator = presetShowsSeparator(toolbarPreset);
  const showReadMore = presetShowsReadMore(toolbarPreset);
  const showHeadings = presetShowsHeadings(toolbarPreset);

  return (
    <div
      className={cn(
        "flex w-full flex-wrap items-center gap-x-1 gap-y-1 rounded-lg border border-slate-200 bg-slate-50/80",
        compact ? "my-0 p-0.5" : "my-2 p-1",
      )}
    >
      <Toggle
        size="default"
        className={iconButtonClass}
        aria-label={vt("bo.tiptap.bold", "Gras")}
        title={vt("bo.tiptap.bold", "Gras")}
        pressed={editor.isActive("bold")}
        onPressedChange={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className={iconClass} />
      </Toggle>
      <Toggle
        size="default"
        className={iconButtonClass}
        aria-label={vt("bo.tiptap.italic", "Italique")}
        title={vt("bo.tiptap.italic", "Italique")}
        pressed={editor.isActive("italic")}
        onPressedChange={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className={iconClass} />
      </Toggle>
      <Toggle
        size="default"
        className={iconButtonClass}
        aria-label={vt("bo.tiptap.underline", "Texte souligné")}
        title={vt("bo.tiptap.underline", "Texte souligné")}
        pressed={editor.isActive("underline")}
        onPressedChange={() =>
          editor.chain().focus().toggleMark("underline").run()
        }
      >
        <UnderlineIcon className={iconClass} />
      </Toggle>

      <ToolbarFontSizeSelect editor={editor} />
      {showFontFamily && <ToolbarFontFamilySelect editor={editor} />}
      <ToolbarColorSelect editor={editor} />

      <ToolbarTextAlignGroup editor={editor} />

      {showSpacing && <ToolbarParagraphSpacingSelect editor={editor} />}

      {showBulletList && (
        <Toggle
          size="default"
          className={iconButtonClass}
          aria-label={vt("bo.tiptap.bulletList", "Liste à puces")}
          title={vt("bo.tiptap.bulletList", "Liste à puces")}
          pressed={editor.isActive("bulletList")}
          onPressedChange={() =>
            editor.chain().focus().toggleBulletList().run()
          }
        >
          <List className={iconClass} />
        </Toggle>
      )}

      {showSeparator && (
        <Toggle
          size="default"
          className={iconButtonClass}
          aria-label={vt("bo.tiptap.horizontalRule", "Séparateur")}
          title={vt("bo.tiptap.horizontalRule", "Séparateur")}
          pressed={editor.isActive("horizontalRule")}
          onPressedChange={() =>
            editor.chain().focus().setHorizontalRule().run()
          }
        >
          <SeparatorHorizontal className={iconClass} />
        </Toggle>
      )}

      {linkPopover}

      {showReadMore && (
        <Toggle
          size="default"
          className={iconButtonClass}
          aria-label={vt("bo.tiptap.readMore", "Lire plus")}
          title={vt(
            "bo.tiptap.readMore",
            "Insérer un « Lire plus » (coupe la remarque publique)",
          )}
          pressed={getReadMorePositions(editor).length > 0}
          onPressedChange={() => toggleReadMore(editor)}
        >
          <ChevronsDown className={iconClass} />
        </Toggle>
      )}

      {showHeadings && (
        <ToolbarHeadingToggles
          editor={editor}
          controlClassName={iconButtonClass}
          iconClassName={iconClass}
        />
      )}

      <Toggle
        size="default"
        className={iconButtonClass}
        aria-label={vt("bo.tiptap.undo", "Annuler")}
        title={vt("bo.tiptap.undo", "Annuler")}
        disabled={!editor.can().undo()}
        onPressedChange={() => editor.chain().focus().undo().run()}
      >
        <Undo className={iconClass} />
      </Toggle>
      <Toggle
        size="default"
        className={iconButtonClass}
        aria-label={vt("bo.tiptap.redo", "Rétablir")}
        title={vt("bo.tiptap.redo", "Rétablir")}
        disabled={!editor.can().redo()}
        onPressedChange={() => editor.chain().focus().redo().run()}
      >
        <Redo className={iconClass} />
      </Toggle>
    </div>
  );
}

export default ToolBar;
